/*
 * Copyright (C) 2020 Reciprocity, Inc - All Rights Reserved
 * Unauthorized use, copying, distribution, displaying, or public performance
 * of this file, via any medium, is strictly prohibited. All information
 * contained herein is proprietary and confidential and may not be shared
 * with any third party without the express written consent of Reciprocity, Inc.
 */

import { postRequest, putRequest } from '../../../utils/client/client';

export const USER_ROLE_QUERY = `
  query UserRole {
    currentUser {
      userRole { value }
    }
  }
`;

export const REQUEST_INFO_QUERY = (requestId: string) => `
  query Request {
    node(id: "${requestId}") {
      ... on Request {
        id
        title {
          value
        }
        description {
          value
        }
        status {
          value
        }
        endDate {
          value
        }
        isOverdue {
          value
        }
      }
    }
  }
`;

export const REQUEST_REVIEWERS_QUERY = (requestId: string) => `
  query Request {
    node(id: "${requestId}") {
      ... on Request {
        reviewers {
          value {
            edges {
              node {
                person {
                  value {
                    email { value }
                    name { value }
                    userRole { value }
                    profileImageUrl { value }
                  }
                }
              }
            }
          }
        }
      }
    }
  }
`;

export function getCSRFToken(store): string | undefined {
  if (store) {
    return store.getters.csrfToken;
  } else {
    return document.body.dataset.csrf;
  }
}

export async function fetchUserRole() {
  let result;
  let error;

  const data = {
    query: USER_ROLE_QUERY,
  };

  try {
    const responseData = await postRequest({ url: '/graphql', data });
    result = responseData;
  } catch (err) {
    error = err;
  }

  return { result, error };
}

export async function fetchRequestInfo(requestId: string) {
  let result;
  let error;

  const data = {
    query: REQUEST_INFO_QUERY(requestId),
  };

  try {
    const responseData = await postRequest({ url: '/graphql', data });
    result = responseData;
  } catch (err) {
    error = err;
  }

  return { result, error };
}

export async function fetchRequestReviewers(requestId: string) {
  let result;
  let error;

  const data = {
    query: REQUEST_REVIEWERS_QUERY(requestId),
  };

  try {
    const responseData = await postRequest({ url: '/graphql', data });
    result = responseData;
  } catch (err) {
    error = err;
  }

  return { result, error };
}

export async function updateRequestStatus(requestId: string, status: string, csrfToken: string) {
  let result;
  let error;

  const url = `/api/v2/crud/Request/${requestId}/status`;
  const data = {
    comment: '',
    ignore_warnings: true,
    value: status,
  };
  const config = {
    headers: {
      'X-CSRF-Token': csrfToken,
    },
  };

  try {
    const responseData = await putRequest({ url, data, ...config });
    result = responseData;
  } catch (err) {
    error = err;
  }

  return { result, error };
}

export function reloadPage(store) {
  if (store) {
    // TODO: when moved to SPA it won't be necessary to reload the page anyway
  } else {
    window.GGRC.zenModal.$emit('reloadPage');
    window.GGRC.zenModal.hideAll();
  }
}

export function notifyCurrentUserFetchFail(store) {
  const alertConfig = {
    type: 'error',
    message: 'There was a problem fetching current user data. Please try again or refresh your page.',
  };

  if (store) {
    return store.dispatch('alerts/show', alertConfig);
  } else {
    window.GGRC.Notify(alertConfig);
  }
}

export function notifyRequestFetchFail(store) {
  const alertConfig = {
    type: 'error',
    message: 'There was a problem fetching data for this request. Please try again or refresh your page.',
  };

  if (store) {
    return store.dispatch('alerts/show', alertConfig);
  } else {
    window.GGRC.Notify(alertConfig);
  }
}

export function notifySubmitFail(store) {
  const alertConfig = {
    type: 'error',
    message: 'Oops, something went wrong. Please try again or refresh your page.',
  };

  if (store) {
    return store.dispatch('alerts/show', alertConfig);
  } else {
    window.GGRC.Notify(alertConfig);
  }
}

export function notifySubmitSuccess(store) {
  const alertConfig = {
    type: 'success',
    message: 'Request has been submitted.',
  };

  if (store) {
    return store.dispatch('alerts/show', alertConfig);
  } else {
    window.GGRC.Notify(alertConfig);
  }
}

export function showOldModalType(store, data) {
  if (store) {
    // TODO: when moved to SPA
  } else {
    window.GGRC.zenModal.hideAll();
    window.GGRC.zenModal.show({
      type: 'ObjectInfo',
      data,
      context: true,
    });
  }
}
